import { FC, useState } from "react";
import { Box, Divider, LinearProgress, Stack } from "@mui/material";
import { useDidMount } from "rooks";
import { GridApi } from "ag-grid-community";
import { SavingsOpportunitiesDataGrid } from "./components/SavingsOpportunitiesDataGrid";
import { SavingsOpportunitiesTableToolbar } from "./components/toolbar/SavingsOpportunitiesTableToolbar";
import { SavingsOpportunitiesMetadata } from "./components/metadata/SavingsOpportunitiesMetadata";
import { SavingsOpportunitiesViewsHierarchy } from "./components/views/SavingsOpportunitiesViewsHierarchy";
import { useSavingsOpportunityIdFromUrl } from "./utils/hooks/useSavingsOpportunityIdFromUrl";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { savingsOpportunitiesLoadingSelector } from "@/store/savings-opportunities/selectors/savingsOpportunitiesLoadingSelector";
import { savingsOpportunitiesViewsHierarchyLoadingSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/loadings/savingsOpportunitiesViewsHierarchyLoadingSelector";
import { savingsOpportunitiesDataSelector } from "@/store/savings-opportunities/selectors/savingsOpportunitiesDataSelector";
import { savingsOpportunityCurrentViewSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/current-view/savingsOpportunityCurrentViewSelector";
import { TestAiSavingOpportunitiesButton } from "@/components/pages/savings-opportunities/TestAiSavingOpportunitiesButton";
import { SavingsOpportunitiesFilters } from "@/components/pages/savings-opportunities/components/SavingsOpportunitiesFilters";
import { initiateSavingsOpportunitiesThunk } from "@/store/savings-opportunities/thunks/initiateSavingsOpportunitiesThunk";
import { savingsOpportunitiesCurrentViewIdSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/current-view/savingsOpportunitiesCurrentViewIdSelector";
import { savingOpportunitiesDefaultHiddenColumns } from "@/components/pages/savings-opportunities/components/savingOpportunitiesDefaultHiddenColumns";
import { AgGridGroupingSelect } from "@/components/pages/common/ag-grid/components/grouping-select/AgGridGroupingSelect";
import { savingsOpportunitiesGroupingOptions } from "@/components/pages/savings-opportunities/utils/constants/savingsOpportunitiesGroupingOptions";
import { SavingsOpportunitiesTableToolbarLeftSide } from "@/components/pages/savings-opportunities/components/toolbar/SavingsOpportunitiesTableToolbarLeftSide";

export const SavingsOpportunities: FC = () => {
  const dispatch = useAppDispatch();

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  useSavingsOpportunityIdFromUrl(gridApi);

  const data = useAppSelector(savingsOpportunitiesDataSelector);
  const currentViewId = useAppSelector(
    savingsOpportunitiesCurrentViewIdSelector,
  );

  const opportunitiesLoading = useAppSelector(
    savingsOpportunitiesLoadingSelector,
  );
  const viewsLoading = useAppSelector(
    savingsOpportunitiesViewsHierarchyLoadingSelector,
  );
  const loading = opportunitiesLoading || viewsLoading;
  const viewId = useAppSelector(savingsOpportunityCurrentViewSelector)?.id;

  useDidMount(() => {
    dispatch(initiateSavingsOpportunitiesThunk());
  });

  return (
    <Stack bgcolor="white" height="100%" key={currentViewId}>
      <SavingsOpportunitiesViewsHierarchy gridApi={gridApi} />

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        p={1}
        borderBottom={1}
        borderColor="grey.300"
      >
        <SavingsOpportunitiesFilters />

        <AgGridGroupingSelect
          gridApi={gridApi}
          groupingOptions={savingsOpportunitiesGroupingOptions}
        />

        <TestAiSavingOpportunitiesButton />
      </Stack>

      {loading && (
        <Box position="absolute" top={0} left={0} right={0}>
          <LinearProgress />
        </Box>
      )}

      <SavingsOpportunitiesMetadata gridApi={gridApi} />

      <Stack py={1}>
        <SavingsOpportunitiesTableToolbar gridApi={gridApi}>
          <SavingsOpportunitiesTableToolbarLeftSide gridApi={gridApi} />
        </SavingsOpportunitiesTableToolbar>
      </Stack>

      <Divider />

      <SavingsOpportunitiesDataGrid
        data={data}
        saveGridState
        gridApi={gridApi}
        viewId={viewId}
        setGridApi={setGridApi}
        hiddenColumns={savingOpportunitiesDefaultHiddenColumns}
      />
    </Stack>
  );
};
